import { DeviceReport, UnassignedDeviceReport } from "@/data/models/DeviceReport";

/**
 * Turns a device report payload into something renderable.
 *
 * Extracted from the viewer so it can be unit-tested directly: the payload comes from the
 * production test rig, whose steps, captured fields and firmware list change with every hardware
 * revision, so **nothing here may assume a shape**. Every function tolerates a missing, renamed or
 * differently-typed field, because the alternative is a screen that shows nothing after the next
 * rig update.
 */

/** How deep the generic tree renders before it stops. */
export const MAX_TREE_DEPTH = 12;

/** Placeholder for a value the payload does not carry. */
export const ABSENT_VALUE = "—";

/** One row of the steps table. */
export interface DisplayStep {
  step: string;
  comment: string;
  status: string;
  detail: string;
  duration: string;
}

/** One row of the generic tree. */
export interface TreeEntry {
  key: string;
  label: string;
  isLeaf: boolean;
  value: string;
  summary: string;
  raw: unknown;
}

/** A payload value as display text, with a placeholder for absent ones. */
export function toDisplayText(value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return ABSENT_VALUE;
  }

  return String(value);
}

/**
 * A step duration in seconds, which is the scale the rig's steps actually run at.
 *
 * Anything that is not a finite number becomes the placeholder rather than `NaN s`.
 */
export function formatStepDuration(durationInMilliseconds: unknown): string {
  if (typeof durationInMilliseconds !== "number" || !Number.isFinite(durationInMilliseconds)) {
    return ABSENT_VALUE;
  }

  return `${(durationInMilliseconds / 1000).toFixed(1)} s`;
}

/**
 * The payload's `steps` as table rows.
 *
 * Returns an empty list for a payload without a usable `steps` array, which is what makes the table
 * disappear rather than render empty. Entries that are not objects are dropped: a rig that ever
 * emits a list of strings should show nothing in a table of fields, not a row of placeholders.
 */
export function toDisplaySteps(payload: unknown): DisplayStep[] {
  const steps = (payload as Record<string, unknown> | null)?.steps;

  if (!Array.isArray(steps)) {
    return [];
  }

  return steps
    .filter((step) => step !== null && typeof step === "object" && !Array.isArray(step))
    .map((step) => {
      const source = step as Record<string, unknown>;

      return {
        step: toDisplayText(source.step),
        comment: toDisplayText(source.comment),
        // Empty rather than a placeholder: the viewer renders this as a chip, and a chip saying
        // "—" reads as a status of its own.
        status: typeof source.status === "string" ? source.status : "",
        detail: toDisplayText(source.detail),
        duration: formatStepDuration(source.duration_ms),
      };
    });
}

/** The payload's overall result, or an empty string when it carries none. */
export function getOverallResult(payload: unknown): string {
  const value = (payload as Record<string, unknown> | null)?.overall_result;

  return typeof value === "string" ? value : "";
}

/** Whether a pass/fail-style value counts as a pass. Anything else is shown as a failure. */
export function isPassResult(result: string | null | undefined): boolean {
  return (result ?? "").toLowerCase() === "pass";
}

/**
 * Whether a report is the device's **initial** test.
 *
 * Derived, never stored: "initial" is a function of ordering, so the oldest report for a device is
 * its initial test. `reports` is the newest-first list the repository returns, so that is the last
 * entry.
 *
 * Only ever a display hint. A report still waiting in the unassigned area is not in this list, so an
 * older one may yet appear and take the label.
 */
export function isInitialReport(reports: DeviceReport[], report: DeviceReport): boolean {
  return reports.length > 0 && reports[reports.length - 1].id === report.id;
}

/** What a branch says on its own line: how much is inside it. */
function summarise(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.length}]`;
  }

  return `{${Object.keys(value as Record<string, unknown>).length}}`;
}

/**
 * One level of the generic tree.
 *
 * Depth is capped rather than trusted: a payload is external input, and although JSON cannot express
 * a cycle, an absurdly nested one would otherwise recurse until the renderer dies. The cap sits far
 * above any real report.
 */
export function toTreeEntries(value: unknown, depth: number): TreeEntry[] {
  if (depth >= MAX_TREE_DEPTH || value === null || typeof value !== "object") {
    return [];
  }

  const source = value as Record<string, unknown>;
  const isArray = Array.isArray(value);

  return Object.keys(source).map((key) => {
    const raw = source[key];
    const isLeaf = raw === null || typeof raw !== "object";

    return {
      key,
      // An array index reads better as "1." than as "0". A key keeps its own spelling: these are the
      // rig's field names, and renaming them would make a report impossible to compare against the
      // file the operator has on disk.
      label: isArray ? `${Number(key) + 1}.` : key,
      isLeaf,
      value: isLeaf ? toDisplayText(raw) : "",
      summary: isLeaf ? "" : summarise(raw),
      raw,
    };
  });
}

/**
 * One row of the results list, whichever source it came from.
 *
 * Assigned and unassigned reports are genuinely different records — one is a Firestore document,
 * the other is an object in Cloud Storage with custom metadata — and the screen shows them in one
 * list, so they are flattened to a single shape here rather than branching in the template.
 */
export interface DeviceReportRow {
  /** Unique within a list; the storage location, which is unique by construction. */
  key: string;
  /** Report id, which the viewer needs to read the captured time back. */
  id: string;
  isAssigned: boolean;
  serialNo: string | null;
  macAddress: string | null;
  deviceCategory: string | null;
  reportType: string;
  reportedAt: Date | null;
  overallResult: string | null;
  createdBy: string | null;
  /** What the viewer is addressed by: a storageLocation or a plain object name. */
  location: string;
}

/** A row for a report attached to a device. */
export function toRowFromAssigned(report: DeviceReport): DeviceReportRow {
  return {
    key: report.storageLocation,
    id: report.id,
    isAssigned: true,
    serialNo: report.serialNo,
    macAddress: report.macAddress ?? null,
    deviceCategory: report.deviceCategory ?? null,
    reportType: report.reportType,
    reportedAt: report.reportedAt ? report.reportedAt.toDate() : null,
    overallResult: report.overallResult ?? null,
    createdBy: report.createdBy ?? null,
    location: report.storageLocation,
  };
}

/** A row for a report still waiting in the unassigned area. */
export function toRowFromUnassigned(report: UnassignedDeviceReport): DeviceReportRow {
  return {
    key: report.objectName,
    id: report.id,
    isAssigned: false,
    serialNo: report.serialNo,
    macAddress: report.macAddress,
    // Unassigned reports have no category: nothing has resolved them to a device yet.
    deviceCategory: null,
    reportType: report.reportType,
    reportedAt: report.reportedAt,
    overallResult: report.overallResult,
    createdBy: report.createdBy,
    location: report.objectName,
  };
}

/**
 * Newest first, with undatable rows last.
 *
 * A row whose timestamp could not be read sorts to the bottom rather than to the top, where a
 * missing date would otherwise masquerade as the most recent report.
 */
export function sortRowsByReportedAt(rows: DeviceReportRow[]): DeviceReportRow[] {
  return [...rows].sort((first, second) => {
    if (!first.reportedAt || !second.reportedAt) {
      return Number(Boolean(second.reportedAt)) - Number(Boolean(first.reportedAt));
    }

    return second.reportedAt.getTime() - first.reportedAt.getTime();
  });
}
